import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useApiData from "../../services/useApiData.js";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import api from "../../services/axiosInterceptor.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import ConfirmDeleteDialog from "../Helpers/ConfirmDeleteDialog.jsx";
import GetData from "../../services/GetData.js";
import {
    SingleSelectField,
    DimensionMultiSelect,
    DimensionSingleSelect,
    LookupField,
    SkuTextField,
} from "./TypedFields.jsx";
import SelectOptionsDialog from "./SelectOptionsDialog.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { PRODUCT_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import {
    catalogDataGridSx,
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "./catalogGridStyles.js";
import AuthenticatedAssetImage from "../Helpers/AuthenticatedAssetImage.jsx";

const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

function revokeUrl(url) {
    if (url && String(url).startsWith("blob:")) {
        URL.revokeObjectURL(url);
    }
}

const EMPTY_FORM = {
    itemName: "",
    internalMoniker: "",
    azCategory: "",
    ebayCategory: "",
    gender: "",
    conditionGrade: "",
    packagingType: "",
    partialPackEligible: "",
    ebayShippingPolicy: "",
    ebayDescriptionTemplate: "",
    descriptionTemplateValueEbayConfig: "",
    ebayConditionField: "",
    ebayPublicInterfaceView: "",
    walmartBlocked: "",
    brand: [],
    size: [],
    colorFlavor: [],
    model: [],
    statusChecks: [],
    shippingClass1x: [],
    shippingClass2x: [],
    shippingClass3x: [],
    shippingClass4x: [],
    upc: "",
    gtin: "",
    amazonUrl: "",
    walmartUrl: "",
    totalQuantity: "",
    innerPacks: "",
    viablePackCount: "",
    weightLbs: "",
    heightIn: "",
    widthIn: "",
    depthIn: "",
    msrpUsd: "",
    velocityAmazonSalesPerMonth: "",
    vistaMaxPrice: "",
    walmartPrice: "",
    ebayPrice: "",
    walmartLinkedSku: "",
    ebayLinkedSku: "",
    volumePricing2x: "",
    volumePricing3x: "",
    volumePricing4x: "",
    inspectionWizardBuiltSku: false,
    notes: "",
};

const EMPTY_COMPUTED = {
    listingField: "",
    internalSkuId: "",
    asin: "",
    wpid: "",
    macMaxPrice: "",
    bidftaMaxPrice: "",
    ffPricing: "",
    fbMktPricing: "",
    purchasePriceEbay: "",
    estShippingCost: "",
    costRollup1x: "",
    costRollup2x: "",
    costRollup3x: "",
    costRollup4x: "",
    suggestedEbayPrice: "",
    suggestedWalmartPrice: "",
    suggestedMargin: "",
    vistaPackPrice: "",
    helperFlagWalmartAutomation: "",
    listedPlatforms: "",
    availableQuantityEbay: "",
    availableQuantityWalmart: "",
    ebayQuant30PlusDays: "",
    walmartQuant30PlusDays: "",
    ebayOlderListingAge: "",
    walmartOlderListingAge: "",
    totalQuantOnHand: "",
    positiveExampleCount: "",
    negativeExampleCount: "",
};

function toNumberOrNull(value) {
    if (value === "" || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function namesOf(value) {
    if (!Array.isArray(value)) return "";
    return value
        .map((entry) => (entry && typeof entry === "object" ? entry.name || "" : ""))
        .filter(Boolean)
        .join(", ");
}

function countOf(value) {
    return Array.isArray(value) ? value.length : 0;
}

function displayNum(value) {
    return value == null || value === "" ? "" : value;
}

function moneyText(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function percentText(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return `${(n * 100).toFixed(1)}%`;
}

function dateText(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString();
}

function createdByText(value) {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value.name || value.email || "";
}

function idList(arr) {
    return (arr || []).map((entry) => (typeof entry === "object" ? entry._id : entry)).filter(Boolean);
}

function wrapCell(value) {
    const text = value == null || value === "" ? "" : String(value);
    if (!text) {
        return (
            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                —
            </Typography>
        );
    }
    return (
        <Tooltip title={text} enterDelay={300} placement="top-start">
            <Box
                component="span"
                sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                    display: "block",
                }}
            >
                {text}
            </Box>
        </Tooltip>
    );
}

function conditionChip(value) {
    const text = value == null || value === "" ? "" : String(value);
    if (!text) return wrapCell("");
    return (
        <Chip
            size="small"
            label={text}
            sx={{
                height: 24,
                fontWeight: 600,
                fontSize: 12,
                borderRadius: "8px",
                backgroundColor: "#f1f5f9",
                color: "#475569",
            }}
        />
    );
}

const LISTED_CHIP_SX = {
    Walmart: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
    eBay: { backgroundColor: "#e3f2fd", color: "#1565c0" },
};

function listedPlatformChips(platforms) {
    const list = Array.isArray(platforms) ? platforms.filter(Boolean) : [];
    if (!list.length) return wrapCell("");
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 0.5 }}>
            {list.map((platform) => (
                <Chip
                    key={platform}
                    size="small"
                    label={platform}
                    sx={{
                        height: 22,
                        fontWeight: 600,
                        fontSize: 11,
                        borderRadius: "8px",
                        ...(LISTED_CHIP_SX[platform] || {
                            backgroundColor: "#f1f5f9",
                            color: "#475569",
                        }),
                    }}
                />
            ))}
        </Box>
    );
}

export default function ProductGrid() {
    const [rows, setRows] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [rowCount, setRowCount] = useState(0);
    const [selectionModel, setSelectionModel] = useState({
        type: "include",
        ids: new Set(),
    });
    const selectedIds = useMemo(
        () => [...(selectionModel?.ids ?? [])],
        [selectionModel]
    );
    const [dialogOpen, setDialogOpen] = useState(false);
    const [optionEditor, setOptionEditor] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [computed, setComputed] = useState(EMPTY_COMPUTED);
    const [heroPreview, setHeroPreview] = useState(null);
    const [heroFile, setHeroFile] = useState(null);
    const [heroLocalUrl, setHeroLocalUrl] = useState("");
    const [supportingFiles, setSupportingFiles] = useState([]);
    const [existingSupporting, setExistingSupporting] = useState([]);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const heroInputRef = useRef(null);
    const supportingInputRef = useRef(null);

    const clearLocalPhotos = () => {
        revokeUrl(heroLocalUrl);
        supportingFiles.forEach((entry) => revokeUrl(entry.url));
        setHeroFile(null);
        setHeroLocalUrl("");
        setSupportingFiles([]);
    };

    useEffect(() => {
        return () => {
            revokeUrl(heroLocalUrl);
            supportingFiles.forEach((entry) => revokeUrl(entry.url));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke only on unmount
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            setSearchQ(searchInput.trim());
            setPaginationModel((prev) =>
                prev.page === 0 ? prev : { ...prev, page: 0 }
            );
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    const { data: fieldMeta, refetch: refetchFieldMeta } = useApiData(
        "api/broylesllc/catalog-field-meta"
    );
    const productMeta = fieldMeta?.product || {};
    const filterFields = useMemo(() => PRODUCT_FILTER_FIELDS(productMeta), [productMeta]);
    const { apply: applyFilter, filterActive, barProps: filterBar } = useGridFilter(filterFields);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(filterActive ? 1 : paginationModel.page + 1),
            pageSize: String(filterActive ? 500 : paginationModel.pageSize),
        });
        if (searchQ) params.set("q", searchQ);
        return `api/broylesllc/products?${params.toString()}`;
    }, [paginationModel.page, paginationModel.pageSize, searchQ, filterActive]);

    const { data, error, load, isLoading, refetch } = useApiData(listUrl);

    const { data: brandsData } = useApiData("api/broylesllc/brands?limit=500");
    const { data: sizesData } = useApiData("api/broylesllc/sizes?limit=500");
    const { data: colorsData } = useApiData("api/broylesllc/color-flavors?limit=500");
    const { data: modelsData } = useApiData("api/broylesllc/models?limit=500");
    const { data: statusData } = useApiData("api/broylesllc/status-checks?limit=500");
    const { data: shippingData } = useApiData("api/broylesllc/shipping-classes?limit=500");

    const brandOptions = brandsData?.items || [];
    const sizeOptions = sizesData?.items || [];
    const colorOptions = colorsData?.items || [];
    const modelOptions = modelsData?.items || [];
    const statusOptions = statusData?.items || [];
    const shippingOptions = shippingData?.items || [];

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : 0);
        setRows(
            items.map((p) => ({
                id: p._id,
                listingField: p.listingField || "",
                itemName: p.itemName || "",
                brand: namesOf(p.brand),
                heroPhoto: countOf(p.heroPhoto) || "",
                supportingPhotos: countOf(p.supportingPhotos) || "",
                azCategory: p.azCategory || "",
                ebayCategory: p.ebayCategory || "",
                size: namesOf(p.size),
                totalQuantity: displayNum(p.totalQuantity),
                innerPacks: displayNum(p.innerPacks),
                partialPackEligible: p.partialPackEligible || "",
                colorFlavor: namesOf(p.colorFlavor),
                model: namesOf(p.model),
                gender: p.gender || "",
                conditionGrade: p.conditionGrade || "",
                statusChecks: namesOf(p.statusChecks),
                positiveExamples: countOf(p.positiveExamples) || "",
                negativeExamples: countOf(p.negativeExamples) || "",
                upc: p.upc || "",
                amazonUrl: p.amazonUrl || "",
                asin: p.asin || "",
                gtin: p.gtin || "",
                walmartUrl: p.walmartUrl || "",
                wpid: p.wpid || "",
                msrpUsd: displayNum(p.msrpUsd),
                velocityAmazonSalesPerMonth: displayNum(p.velocityAmazonSalesPerMonth),
                vistaMaxPrice: displayNum(p.vistaMaxPrice),
                macMaxPrice: displayNum(p.macMaxPrice),
                bidftaMaxPrice: displayNum(p.bidftaMaxPrice),
                ffPricing: displayNum(p.ffPricing),
                fbMktPricing: displayNum(p.fbMktPricing),
                purchasePriceEbay: displayNum(p.purchasePriceEbay),
                weightLbs: displayNum(p.weightLbs),
                heightIn: displayNum(p.heightIn),
                widthIn: displayNum(p.widthIn),
                depthIn: displayNum(p.depthIn),
                shippingClass1x: namesOf(p.shippingClass1x),
                costRollup1x: displayNum(p.costRollup1x),
                shippingClass2x: namesOf(p.shippingClass2x),
                costRollup2x: displayNum(p.costRollup2x),
                shippingClass3x: namesOf(p.shippingClass3x),
                costRollup3x: displayNum(p.costRollup3x),
                shippingClass4x: namesOf(p.shippingClass4x),
                costRollup4x: displayNum(p.costRollup4x),
                packagingType: p.packagingType || "",
                estShippingCost: displayNum(p.estShippingCost),
                ebayShippingPolicy: p.ebayShippingPolicy || "",
                ebayDescriptionTemplate: p.ebayDescriptionTemplate || "",
                walmartBlocked: p.walmartBlocked || "",
                suggestedEbayPrice: displayNum(p.suggestedEbayPrice),
                suggestedWalmartPrice: displayNum(p.suggestedWalmartPrice),
                suggestedMargin: displayNum(p.suggestedMargin),
                catalogItems: countOf(p.catalogItemAirtableIds) || "",
                createdBy: createdByText(p.createdByAirtable),
                viablePackCount: displayNum(p.viablePackCount),
                vistaPackPrice: displayNum(p.vistaPackPrice),
                createdTime: dateText(p.airtableCreatedTime || p.createdAt),
                internalSkuId: p.internalSkuId || "",
                walmartSkuLink: p.walmartLinkedSku || countOf(p.walmartSkuLinkAirtableIds) || "",
                ebaySkuLink: p.ebayLinkedSku || countOf(p.ebaySkuLinkAirtableIds) || "",
                listedPlatforms: Array.isArray(p.listedPlatforms) ? p.listedPlatforms : [],
                listedPlatformsText: Array.isArray(p.listedPlatforms)
                    ? p.listedPlatforms.join(", ")
                    : "",
                walmartPrice: displayNum(p.walmartPrice),
                ebayPrice: displayNum(p.ebayPrice),
                helperFlagWalmartAutomation: p.helperFlagWalmartAutomation || "",
                availableQuantityEbay: displayNum(p.availableQuantityEbay),
                availableQuantityWalmart: displayNum(p.availableQuantityWalmart),
                descriptionTemplateValueEbayConfig: p.descriptionTemplateValueEbayConfig || "",
                ebayConditionField: p.ebayConditionField || "",
                volumePricing2x: displayNum(p.volumePricing2x),
                volumePricing3x: displayNum(p.volumePricing3x),
                volumePricing4x: displayNum(p.volumePricing4x),
                table21: countOf(p.table21AirtableIds) || "",
                ebayPublicInterfaceView: p.ebayPublicInterfaceView || "",
                ebayQuant30PlusDays: displayNum(p.ebayQuant30PlusDays),
                walmartQuant30PlusDays: displayNum(p.walmartQuant30PlusDays),
                ebayOlderListingAge: displayNum(p.ebayOlderListingAge),
                walmartOlderListingAge: displayNum(p.walmartOlderListingAge),
                totalQuantOnHand: displayNum(p.totalQuantOnHand),
                inspectionWizardBuiltSku: p.inspectionWizardBuiltSku ? "Yes" : "",
            }))
        );
    }, [data]);

    const filteredRows = useMemo(() => applyFilter(rows), [rows, applyFilter]);

    useEffect(() => {
        if (filterActive) {
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }
    }, [filterActive]);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.products, {
        sortModel: [],
        columnVisibilityModel: {},
    });

    const baseColumns = useMemo(() => {
        const text = (field, headerName, width = 150) => ({
            field,
            headerName,
            width,
            minWidth: 90,
            renderCell: (params) => wrapCell(params.value),
        });
        const money = (field, headerName, width = 130) => ({
            field,
            headerName,
            width,
            minWidth: 100,
            align: "right",
            headerAlign: "right",
            renderCell: (params) => wrapCell(moneyText(params.value)),
        });
        const number = (field, headerName, width = 110) => ({
            field,
            headerName,
            width,
            minWidth: 80,
            type: "number",
            align: "right",
            headerAlign: "right",
        });
        const percent = (field, headerName) => ({
            field,
            headerName,
            width: 140,
            minWidth: 100,
            align: "right",
            headerAlign: "right",
            renderCell: (params) => wrapCell(percentText(params.value)),
        });
        return [
            { ...text("listingField", "Listing Field", 280), flex: 1.2, minWidth: 200 },
            text("itemName", "Item Name", 220),
            text("brand", "Brand", 140),
            number("heroPhoto", "Hero Photo", 110),
            number("supportingPhotos", "Supporting Photos", 150),
            text("azCategory", "AZ Category", 160),
            text("ebayCategory", "eBay Category", 170),
            text("size", "Size", 120),
            number("totalQuantity", "Total Quantity", 120),
            number("innerPacks", "Inner Packs", 110),
            text("partialPackEligible", "Partial Pack Eligible", 160),
            text("colorFlavor", "Color / Flavor", 160),
            text("model", "Model", 140),
            text("gender", "Gender", 110),
            {
                field: "conditionGrade",
                headerName: "Condition Grade",
                width: 140,
                minWidth: 110,
                renderCell: (params) => conditionChip(params.value),
            },
            text("statusChecks", "Status Checks", 160),
            number("positiveExamples", "Positive Examples", 150),
            number("negativeExamples", "Negative Examples", 160),
            text("upc", "UPC", 140),
            text("amazonUrl", "Amazon URL", 180),
            text("asin", "ASIN", 140),
            text("gtin", "GTIN", 140),
            text("walmartUrl", "Walmart URL", 180),
            text("wpid", "WPID", 140),
            money("msrpUsd", "MSRP (USD)"),
            number("velocityAmazonSalesPerMonth", "Velocity", 110),
            money("vistaMaxPrice", "Vista Max Price", 140),
            money("macMaxPrice", "MAC Max Price", 140),
            money("bidftaMaxPrice", "BidFTA Max Price", 150),
            money("ffPricing", "F+F Pricing"),
            money("fbMktPricing", "FB MKT Pricing", 140),
            money("purchasePriceEbay", "Purchase Price (eBay)", 170),
            number("weightLbs", "Weight (lbs)", 120),
            number("heightIn", "Height (in)", 110),
            number("widthIn", "Width (in)", 110),
            number("depthIn", "Depth (in)", 110),
            text("shippingClass1x", "1x Shipping Class", 170),
            money("costRollup1x", "Cost Rollup 1x", 140),
            text("shippingClass2x", "2x Shipping Class", 170),
            money("costRollup2x", "Cost Rollup 2x", 140),
            text("shippingClass3x", "3x Shipping Class", 170),
            money("costRollup3x", "Cost Rollup 3x", 140),
            text("shippingClass4x", "4x Shipping Class", 170),
            money("costRollup4x", "Cost Rollup 4x", 140),
            text("packagingType", "Packaging Type", 180),
            money("estShippingCost", "Est. Shipping Cost", 160),
            text("ebayShippingPolicy", "eBay Shipping Policy", 190),
            text("ebayDescriptionTemplate", "eBay Description Template", 200),
            text("walmartBlocked", "Walmart Blocked?", 140),
            money("suggestedEbayPrice", "Suggested eBay Price", 170),
            money("suggestedWalmartPrice", "Suggested Walmart Price", 190),
            money("suggestedMargin", "Suggested Margin", 150),
            number("catalogItems", "Invoice to Inventory", 160),
            text("createdBy", "Created By", 140),
            number("viablePackCount", "# of Viable Packs", 140),
            money("vistaPackPrice", "Vista Pack Price", 150),
            text("createdTime", "Created Time", 170),
            text("internalSkuId", "Internal SKU ID", 180),
            {
                field: "listedPlatforms",
                headerName: "Listed On",
                width: 160,
                minWidth: 130,
                sortable: false,
                valueGetter: (_value, row) => row.listedPlatformsText || "",
                renderCell: (params) => listedPlatformChips(params.row.listedPlatforms),
            },
            text("walmartSkuLink", "Walmart SKU Link", 170),
            text("ebaySkuLink", "eBay SKU Link", 160),
            money("walmartPrice", "Walmart Price", 140),
            money("ebayPrice", "eBay Price", 120),
            text("helperFlagWalmartAutomation", "Helper Flag", 120),
            number("availableQuantityEbay", "Available Qty eBay", 150),
            number("availableQuantityWalmart", "Available Qty Walmart", 170),
            text("descriptionTemplateValueEbayConfig", "Description Template", 180),
            text("ebayConditionField", "eBay Condition", 140),
            percent("volumePricing2x", "Volume Pricing 2x"),
            percent("volumePricing3x", "Volume Pricing 3x"),
            percent("volumePricing4x", "Volume Pricing 4x"),
            number("table21", "Table 21", 100),
            text("ebayPublicInterfaceView", "eBay Public Interface", 170),
            number("ebayQuant30PlusDays", "eBay Quant 30+ Days", 160),
            number("walmartQuant30PlusDays", "WM Quant 30+ Days", 160),
            number("ebayOlderListingAge", "eBay Older Listing Age", 170),
            number("walmartOlderListingAge", "WM Older Listing Age", 170),
            number("totalQuantOnHand", "Total Quant on Hand", 160),
            text("inspectionWizardBuiltSku", "Wizard Built", 120),
        ];
    }, []);

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.committedWidths, layout.applyWidths]
    );

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setComputed(EMPTY_COMPUTED);
        setHeroPreview(null);
        setExistingSupporting([]);
        clearLocalPhotos();
        setDialogOpen(true);
    };

    const openEdit = async (idOverride) => {
        const id = idOverride ?? selectedIds[0];
        if (!id) return;
        const { getData } = await GetData(`broylesllc/products/${id}`);
        if (!getData) {
            setSnack({ open: true, color: "error", message: "Could not load product." });
            return;
        }
        const p = getData;
        const asText = (value) => (value == null || value === "" ? "" : String(value));
        setEditingId(p._id);
        setForm({
            itemName: p.itemName || "",
            internalMoniker: p.internalMoniker || "",
            azCategory: p.azCategory || "",
            ebayCategory: p.ebayCategory || "",
            gender: p.gender || "",
            conditionGrade: p.conditionGrade || "",
            packagingType: p.packagingType || "",
            partialPackEligible: p.partialPackEligible || "",
            ebayShippingPolicy: p.ebayShippingPolicy || "",
            ebayDescriptionTemplate: p.ebayDescriptionTemplate || "",
            descriptionTemplateValueEbayConfig: p.descriptionTemplateValueEbayConfig || "",
            ebayConditionField: p.ebayConditionField || "",
            ebayPublicInterfaceView: p.ebayPublicInterfaceView || "",
            walmartBlocked: p.walmartBlocked || "",
            brand: idList(p.brand),
            size: idList(p.size),
            colorFlavor: idList(p.colorFlavor),
            model: idList(p.model),
            statusChecks: idList(p.statusChecks),
            shippingClass1x: idList(p.shippingClass1x),
            shippingClass2x: idList(p.shippingClass2x),
            shippingClass3x: idList(p.shippingClass3x),
            shippingClass4x: idList(p.shippingClass4x),
            upc: p.upc || "",
            gtin: p.gtin || "",
            amazonUrl: p.amazonUrl || "",
            walmartUrl: p.walmartUrl || "",
            totalQuantity: asText(p.totalQuantity),
            innerPacks: asText(p.innerPacks),
            viablePackCount: asText(p.viablePackCount),
            weightLbs: asText(p.weightLbs),
            heightIn: asText(p.heightIn),
            widthIn: asText(p.widthIn),
            depthIn: asText(p.depthIn),
            msrpUsd: asText(p.msrpUsd),
            velocityAmazonSalesPerMonth: asText(p.velocityAmazonSalesPerMonth),
            vistaMaxPrice: asText(p.vistaMaxPrice),
            walmartPrice: asText(p.walmartPrice),
            ebayPrice: asText(p.ebayPrice),
            walmartLinkedSku: p.walmartLinkedSku || "",
            ebayLinkedSku: p.ebayLinkedSku || "",
            volumePricing2x: asText(p.volumePricing2x),
            volumePricing3x: asText(p.volumePricing3x),
            volumePricing4x: asText(p.volumePricing4x),
            inspectionWizardBuiltSku: Boolean(p.inspectionWizardBuiltSku),
            notes: p.notes || "",
        });
        setComputed({
            listingField: p.listingField || "",
            internalSkuId: p.internalSkuId || "",
            asin: p.asin || "",
            wpid: p.wpid || "",
            macMaxPrice: moneyText(p.macMaxPrice),
            bidftaMaxPrice: moneyText(p.bidftaMaxPrice),
            ffPricing: moneyText(p.ffPricing),
            fbMktPricing: moneyText(p.fbMktPricing),
            purchasePriceEbay: moneyText(p.purchasePriceEbay),
            estShippingCost: moneyText(p.estShippingCost),
            costRollup1x: moneyText(p.costRollup1x),
            costRollup2x: moneyText(p.costRollup2x),
            costRollup3x: moneyText(p.costRollup3x),
            costRollup4x: moneyText(p.costRollup4x),
            suggestedEbayPrice: moneyText(p.suggestedEbayPrice),
            suggestedWalmartPrice: moneyText(p.suggestedWalmartPrice),
            suggestedMargin: moneyText(p.suggestedMargin),
            vistaPackPrice: moneyText(p.vistaPackPrice),
            helperFlagWalmartAutomation: p.helperFlagWalmartAutomation || "",
            listedPlatforms: Array.isArray(p.listedPlatforms)
                ? p.listedPlatforms.join(", ")
                : "",
            availableQuantityEbay: asText(p.availableQuantityEbay),
            availableQuantityWalmart: asText(p.availableQuantityWalmart),
            ebayQuant30PlusDays: asText(p.ebayQuant30PlusDays),
            walmartQuant30PlusDays: asText(p.walmartQuant30PlusDays),
            ebayOlderListingAge: asText(p.ebayOlderListingAge),
            walmartOlderListingAge: asText(p.walmartOlderListingAge),
            totalQuantOnHand: asText(p.totalQuantOnHand),
            positiveExampleCount: String(countOf(p.positiveExamples)),
            negativeExampleCount: String(countOf(p.negativeExamples)),
        });
        const hero = Array.isArray(p.heroPhoto)
            ? p.heroPhoto.find((asset) => asset?.gcsObject || asset?.sourceUrl)
            : null;
        setHeroPreview(hero || null);
        setExistingSupporting(
            (Array.isArray(p.supportingPhotos) ? p.supportingPhotos : []).filter(
                (asset) => asset?.gcsObject || asset?.sourceUrl
            )
        );
        clearLocalPhotos();
        setDialogOpen(true);
    };

    const onHeroSelected = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (!String(file.type || "").startsWith("image/")) {
            setSnack({ open: true, color: "warning", message: "Hero photo must be an image." });
            return;
        }
        revokeUrl(heroLocalUrl);
        setHeroFile(file);
        setHeroLocalUrl(URL.createObjectURL(file));
    };

    const clearHeroSelection = () => {
        revokeUrl(heroLocalUrl);
        setHeroFile(null);
        setHeroLocalUrl("");
    };

    const onSupportingSelected = (event) => {
        const list = Array.from(event.target.files || []);
        event.target.value = "";
        if (!list.length) return;
        const images = list.filter((file) => String(file.type || "").startsWith("image/"));
        if (!images.length) {
            setSnack({
                open: true,
                color: "warning",
                message: "Secondary photos must be images.",
            });
            return;
        }
        setSupportingFiles((prev) => [
            ...prev,
            ...images.map((file) => ({
                key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
                file,
                url: URL.createObjectURL(file),
            })),
        ]);
    };

    const removeSupportingAt = (key) => {
        setSupportingFiles((prev) => {
            const next = [];
            for (const entry of prev) {
                if (entry.key === key) {
                    revokeUrl(entry.url);
                } else {
                    next.push(entry);
                }
            }
            return next;
        });
    };

    const setField = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const setSelect = (key) => (value) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const setSingleLink = (key) => (id) =>
        setForm((prev) => ({ ...prev, [key]: id ? [id] : [] }));

    const save = async () => {
        if (!form.itemName.trim()) {
            setSnack({
                open: true,
                color: "error",
                message: "Item Name is required.",
            });
            return;
        }
        setSaving(true);
        const payload = {
            itemName: form.itemName.trim(),
            internalMoniker: form.internalMoniker.trim(),
            azCategory: form.azCategory,
            ebayCategory: form.ebayCategory,
            gender: form.gender,
            conditionGrade: form.conditionGrade,
            packagingType: form.packagingType,
            partialPackEligible: form.partialPackEligible,
            ebayShippingPolicy: form.ebayShippingPolicy,
            ebayDescriptionTemplate: form.ebayDescriptionTemplate,
            descriptionTemplateValueEbayConfig: form.descriptionTemplateValueEbayConfig,
            ebayConditionField: form.ebayConditionField,
            ebayPublicInterfaceView: form.ebayPublicInterfaceView,
            walmartBlocked: form.walmartBlocked,
            brand: form.brand,
            size: form.size,
            colorFlavor: form.colorFlavor,
            model: form.model,
            statusChecks: form.statusChecks,
            shippingClass1x: form.shippingClass1x,
            shippingClass2x: form.shippingClass2x,
            shippingClass3x: form.shippingClass3x,
            shippingClass4x: form.shippingClass4x,
            upc: form.upc.trim(),
            gtin: form.gtin.trim(),
            amazonUrl: form.amazonUrl.trim(),
            walmartUrl: form.walmartUrl.trim(),
            totalQuantity: toNumberOrNull(form.totalQuantity),
            innerPacks: toNumberOrNull(form.innerPacks),
            viablePackCount: toNumberOrNull(form.viablePackCount),
            weightLbs: toNumberOrNull(form.weightLbs),
            heightIn: toNumberOrNull(form.heightIn),
            widthIn: toNumberOrNull(form.widthIn),
            depthIn: toNumberOrNull(form.depthIn),
            msrpUsd: toNumberOrNull(form.msrpUsd),
            velocityAmazonSalesPerMonth: toNumberOrNull(form.velocityAmazonSalesPerMonth),
            vistaMaxPrice: toNumberOrNull(form.vistaMaxPrice),
            volumePricing2x: toNumberOrNull(form.volumePricing2x),
            volumePricing3x: toNumberOrNull(form.volumePricing3x),
            volumePricing4x: toNumberOrNull(form.volumePricing4x),
            inspectionWizardBuiltSku: Boolean(form.inspectionWizardBuiltSku),
            notes: form.notes,
        };

        let result;
        if (editingId) {
            result = await PutData(payload, `broylesllc/products/${editingId}`);
        } else if (heroFile || supportingFiles.length) {
            const formData = new FormData();
            for (const [key, value] of Object.entries(payload)) {
                if (value == null) continue;
                if (Array.isArray(value) || typeof value === "object") {
                    formData.append(key, JSON.stringify(value));
                } else if (typeof value === "boolean") {
                    formData.append(key, value ? "true" : "false");
                } else {
                    formData.append(key, String(value));
                }
            }
            if (heroFile) formData.append("heroPhoto", heroFile, heroFile.name);
            for (const entry of supportingFiles) {
                formData.append("supportingPhotos", entry.file, entry.file.name);
            }
            try {
                const response = await api.post("api/broylesllc/products", formData);
                result = { success: true, data: response.data };
            } catch (err) {
                result = { success: false, error: err };
            }
        } else {
            result = await PostData(payload, "broylesllc/products");
        }

        setSaving(false);
        if (result.success) {
            clearLocalPhotos();
            setDialogOpen(false);
            setSnack({
                open: true,
                color: "success",
                message: editingId ? "Product updated." : "Product created.",
            });
            refetch();
            setSelectionModel({ type: "include", ids: new Set() });
        } else {
            const msg =
                result?.data?.message ||
                result?.error?.response?.data?.message ||
                "Save failed.";
            setSnack({ open: true, color: "error", message: msg });
        }
    };

    const deleteTargets = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id)))
            .map((row) => ({
                key: String(row.id),
                label:
                    row.listingField ||
                    row.internalSkuId ||
                    row.itemName ||
                    String(row.id),
            }));
    }, [rows, selectedIds]);

    const confirmDelete = async () => {
        if (!selectedIds.length) return;
        setDeleting(true);
        try {
            await Promise.all(
                selectedIds.map((id) =>
                    api.delete(`api/broylesllc/products/${id}`)
                )
            );
            setDeleteDialogOpen(false);
            setSnack({ open: true, color: "success", message: "Deleted." });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } catch (err) {
            setSnack({
                open: true,
                color: "error",
                message: err?.response?.data?.message || "Delete failed.",
            });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            {(error || (!load && !isLoading && data == null)) && (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 0.5 }}>
                    {error ? "Failed to load products." : null}
                </Typography>
            )}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search SKU, name, ASIN…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">
                            {selectedIds.length} selected
                        </span>
                    ) : null}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || deleting}
                        startIcon={
                            deleting ? (
                                <CircularProgress size={14} color="error" />
                            ) : (
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={catalogDangerButtonSx}
                    >
                        Delete
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add Product
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load || isLoading)}
                        rows={filteredRows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortModel={layout.sortModel}
                        onSortModelChange={layout.onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onRowDoubleClick={(params) => openEdit(params.id)}
                        paginationMode={filterActive ? "client" : "server"}
                        rowCount={filterActive ? filteredRows.length : rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slotProps={appDataGridSlotProps}
                        sx={catalogDataGridSx}
                    />
                </div>
            </div>

            <Dialog
                open={dialogOpen}
                onClose={() => {
                    clearLocalPhotos();
                    setDialogOpen(false);
                }}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: { borderRadius: "14px" },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                    {editingId ? "Edit Product" : "Add Product"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.5,
                            pt: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                gridColumn: "1 / -1",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                p: 1.5,
                                borderRadius: "12px",
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#f8fafc",
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1c2430" }}>
                                Product photos
                            </Typography>
                            <Typography sx={{ mt: -1, fontSize: 12, color: "#64748B" }}>
                                {editingId
                                    ? "Photos already on this product. Use Add Product to upload Hero and Secondary images on create."
                                    : "Add a Hero image and optional Secondary images for marketplace listings."}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "flex-start",
                                    gap: 1.5,
                                }}
                            >
                                <Box sx={{ minWidth: 160 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#5c6570", mb: 0.75 }}>
                                        Hero
                                    </Typography>
                                    {heroLocalUrl ? (
                                        <Box sx={{ position: "relative", width: 96, height: 96 }}>
                                            <Box
                                                component="img"
                                                src={heroLocalUrl}
                                                alt="Hero preview"
                                                sx={{
                                                    width: 96,
                                                    height: 96,
                                                    objectFit: "cover",
                                                    borderRadius: "10px",
                                                    border: "1px solid #e2e8f0",
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                            {!editingId ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={clearHeroSelection}
                                                    sx={{
                                                        position: "absolute",
                                                        top: -8,
                                                        right: -8,
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #e2e8f0",
                                                        "&:hover": { backgroundColor: "#f8fafc" },
                                                    }}
                                                >
                                                    <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            ) : null}
                                        </Box>
                                    ) : heroPreview ? (
                                        <AuthenticatedAssetImage
                                            asset={heroPreview}
                                            alt={form.itemName || "Hero photo"}
                                            sx={{
                                                width: 96,
                                                height: 96,
                                                borderRadius: "10px",
                                                border: "1px solid #e2e8f0",
                                                backgroundColor: "#fff",
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: 96,
                                                height: 96,
                                                borderRadius: "10px",
                                                border: "1px dashed #cbd5e1",
                                                backgroundColor: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#94a3b8",
                                            }}
                                        >
                                            <ImageOutlinedIcon fontSize="small" />
                                        </Box>
                                    )}
                                    {!editingId ? (
                                        <>
                                            <input
                                                ref={heroInputRef}
                                                type="file"
                                                accept={IMAGE_ACCEPT}
                                                hidden
                                                onChange={onHeroSelected}
                                            />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ ...catalogQuietButtonSx, mt: 1 }}
                                                onClick={() => heroInputRef.current?.click()}
                                            >
                                                {heroLocalUrl ? "Replace hero" : "Upload hero"}
                                            </Button>
                                        </>
                                    ) : null}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 220 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#5c6570", mb: 0.75 }}>
                                        Secondary
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {existingSupporting.map((asset, index) => (
                                            <AuthenticatedAssetImage
                                                key={asset.gcsObject || asset.sourceUrl || index}
                                                asset={asset}
                                                alt={`Secondary ${index + 1}`}
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: "10px",
                                                    border: "1px solid #e2e8f0",
                                                    backgroundColor: "#fff",
                                                }}
                                            />
                                        ))}
                                        {supportingFiles.map((entry) => (
                                            <Box
                                                key={entry.key}
                                                sx={{ position: "relative", width: 72, height: 72 }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={entry.url}
                                                    alt={entry.file.name}
                                                    sx={{
                                                        width: 72,
                                                        height: 72,
                                                        objectFit: "cover",
                                                        borderRadius: "10px",
                                                        border: "1px solid #e2e8f0",
                                                        backgroundColor: "#fff",
                                                    }}
                                                />
                                                {!editingId ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeSupportingAt(entry.key)}
                                                        sx={{
                                                            position: "absolute",
                                                            top: -8,
                                                            right: -8,
                                                            backgroundColor: "#fff",
                                                            border: "1px solid #e2e8f0",
                                                            "&:hover": { backgroundColor: "#f8fafc" },
                                                        }}
                                                    >
                                                        <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                ) : null}
                                            </Box>
                                        ))}
                                        {!editingId &&
                                            !supportingFiles.length &&
                                            !existingSupporting.length ? (
                                            <Box
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: "10px",
                                                    border: "1px dashed #cbd5e1",
                                                    backgroundColor: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#94a3b8",
                                                }}
                                            >
                                                <ImageOutlinedIcon fontSize="small" />
                                            </Box>
                                        ) : null}
                                    </Box>
                                    {!editingId ? (
                                        <>
                                            <input
                                                ref={supportingInputRef}
                                                type="file"
                                                accept={IMAGE_ACCEPT}
                                                multiple
                                                hidden
                                                onChange={onSupportingSelected}
                                            />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ ...catalogQuietButtonSx, mt: 1 }}
                                                onClick={() => supportingInputRef.current?.click()}
                                            >
                                                Add secondary
                                            </Button>
                                        </>
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>
                        <Typography sx={{ gridColumn: "1 / -1", fontWeight: 700, fontSize: 13, color: "#1c2430" }}>
                            On this SKU
                        </Typography>
                        <Typography sx={{ gridColumn: "1 / -1", mt: -1, fontSize: 12, color: "#64748B" }}>
                            Type a value, or pick a choice. Choose None to clear a select.
                        </Typography>
                        <SkuTextField label="Item Name" value={form.itemName} onChange={setField("itemName")} required />
                        <SkuTextField label="Internal Moniker" value={form.internalMoniker} onChange={setField("internalMoniker")} />
                        <SingleSelectField label="AZ Category" value={form.azCategory} onChange={setSelect("azCategory")} choices={productMeta.azCategory?.choices || []} onManage={() => setOptionEditor({ fieldKey: "azCategory", label: "AZ Category" })} />
                        <SingleSelectField label="eBay Category" value={form.ebayCategory} onChange={setSelect("ebayCategory")} choices={productMeta.ebayCategory?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayCategory", label: "eBay Category" })} />
                        <SingleSelectField label="Gender" value={form.gender} onChange={setSelect("gender")} choices={productMeta.gender?.choices || []} onManage={() => setOptionEditor({ fieldKey: "gender", label: "Gender" })} />
                        <SingleSelectField label="Condition Grade" value={form.conditionGrade} onChange={setSelect("conditionGrade")} choices={productMeta.conditionGrade?.choices || []} onManage={() => setOptionEditor({ fieldKey: "conditionGrade", label: "Condition Grade" })} />
                        <SingleSelectField label="Packaging Type" value={form.packagingType} onChange={setSelect("packagingType")} choices={productMeta.packagingType?.choices || []} onManage={() => setOptionEditor({ fieldKey: "packagingType", label: "Packaging Type" })} />
                        <SingleSelectField label="Partial Pack Eligible" value={form.partialPackEligible} onChange={setSelect("partialPackEligible")} choices={productMeta.partialPackEligible?.choices || []} onManage={() => setOptionEditor({ fieldKey: "partialPackEligible", label: "Partial Pack Eligible" })} />
                        <SingleSelectField label="eBay Shipping Policy" value={form.ebayShippingPolicy} onChange={setSelect("ebayShippingPolicy")} choices={productMeta.ebayShippingPolicy?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayShippingPolicy", label: "eBay Shipping Policy" })} />
                        <SingleSelectField label="eBay Description Template" value={form.ebayDescriptionTemplate} onChange={setSelect("ebayDescriptionTemplate")} choices={productMeta.ebayDescriptionTemplate?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayDescriptionTemplate", label: "eBay Description Template" })} />
                        <SingleSelectField label="Description Template Value" value={form.descriptionTemplateValueEbayConfig} onChange={setSelect("descriptionTemplateValueEbayConfig")} choices={productMeta.descriptionTemplateValueEbayConfig?.choices || []} onManage={() => setOptionEditor({ fieldKey: "descriptionTemplateValueEbayConfig", label: "Description Template Value" })} />
                        <SingleSelectField label="eBay Condition Field" value={form.ebayConditionField} onChange={setSelect("ebayConditionField")} choices={productMeta.ebayConditionField?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayConditionField", label: "eBay Condition Field" })} />
                        <SingleSelectField label="eBay Public Interface View" value={form.ebayPublicInterfaceView} onChange={setSelect("ebayPublicInterfaceView")} choices={productMeta.ebayPublicInterfaceView?.choices || []} onManage={() => setOptionEditor({ fieldKey: "ebayPublicInterfaceView", label: "eBay Public Interface View" })} />
                        <SingleSelectField label="Walmart Blocked?" value={form.walmartBlocked} onChange={setSelect("walmartBlocked")} choices={productMeta.walmartBlocked?.choices || []} onManage={() => setOptionEditor({ fieldKey: "walmartBlocked", label: "Walmart Blocked?" })} />
                        <SkuTextField label="Total Quantity" type="number" value={form.totalQuantity} onChange={setField("totalQuantity")} />
                        <SkuTextField label="Inner Packs" type="number" value={form.innerPacks} onChange={setField("innerPacks")} />
                        <SkuTextField label="# of Viable Packs" type="number" value={form.viablePackCount} onChange={setField("viablePackCount")} />
                        <SkuTextField label="UPC" value={form.upc} onChange={setField("upc")} />
                        <SkuTextField label="GTIN" value={form.gtin} onChange={setField("gtin")} />
                        <SkuTextField label="Amazon URL" value={form.amazonUrl} onChange={setField("amazonUrl")} />
                        <SkuTextField label="Walmart URL" value={form.walmartUrl} onChange={setField("walmartUrl")} />
                        <SkuTextField label="MSRP (USD)" type="number" value={form.msrpUsd} onChange={setField("msrpUsd")} />
                        <SkuTextField label="Velocity (Amazon / month)" type="number" value={form.velocityAmazonSalesPerMonth} onChange={setField("velocityAmazonSalesPerMonth")} />
                        <SkuTextField label="Vista Max Price" type="number" value={form.vistaMaxPrice} onChange={setField("vistaMaxPrice")} />
                        <SkuTextField label="Weight (lbs)" type="number" value={form.weightLbs} onChange={setField("weightLbs")} />
                        <SkuTextField label="Height (in)" type="number" value={form.heightIn} onChange={setField("heightIn")} />
                        <SkuTextField label="Width (in)" type="number" value={form.widthIn} onChange={setField("widthIn")} />
                        <SkuTextField label="Depth (in)" type="number" value={form.depthIn} onChange={setField("depthIn")} />
                        <SkuTextField label="Volume Pricing 2x" type="number" value={form.volumePricing2x} onChange={setField("volumePricing2x")} hint="Decimal on the SKU. 0.15 is 15%." />
                        <SkuTextField label="Volume Pricing 3x" type="number" value={form.volumePricing3x} onChange={setField("volumePricing3x")} />
                        <SkuTextField label="Volume Pricing 4x" type="number" value={form.volumePricing4x} onChange={setField("volumePricing4x")} />
                        <FormControlLabel
                            sx={{ alignSelf: "end", m: 0 }}
                            control={
                                <Checkbox
                                    checked={Boolean(form.inspectionWizardBuiltSku)}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            inspectionWizardBuiltSku: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Inspection Wizard Built"
                        />
                        <SkuTextField label="Notes" value={form.notes} onChange={setField("notes")} multiline minRows={2} sx={{ gridColumn: { sm: "1 / -1" } }} />

                        <Typography sx={{ gridColumn: "1 / -1", mt: 1, fontWeight: 700, fontSize: 13, color: "#1c2430" }}>
                            Linked from other tables
                        </Typography>
                        <Typography sx={{ gridColumn: "1 / -1", mt: -1, fontSize: 12, color: "#64748B" }}>
                            Search a record to link it. Clear the field to remove the link.
                        </Typography>
                        <DimensionSingleSelect label="Brand" valueId={form.brand[0] || ""} options={brandOptions} onChange={setSingleLink("brand")} hint="Brand table" />
                        <DimensionSingleSelect label="Size" valueId={form.size[0] || ""} options={sizeOptions} onChange={setSingleLink("size")} hint="Size table" />
                        <DimensionSingleSelect label="Color / Flavor / Variety" valueId={form.colorFlavor[0] || ""} options={colorOptions} onChange={setSingleLink("colorFlavor")} hint="Color / flavor table" />
                        <DimensionSingleSelect label="Model" valueId={form.model[0] || ""} options={modelOptions} onChange={setSingleLink("model")} hint="Model table" />
                        <DimensionMultiSelect label="Status Checks" valueIds={form.statusChecks} options={statusOptions} onChange={setSelect("statusChecks")} hint="Status Checks table. Add or remove links." />
                        <DimensionSingleSelect label="1x Shipping Class" valueId={form.shippingClass1x[0] || ""} options={shippingOptions} onChange={setSingleLink("shippingClass1x")} hint="Shipping class table" />
                        <DimensionSingleSelect label="2x Shipping Class" valueId={form.shippingClass2x[0] || ""} options={shippingOptions} onChange={setSingleLink("shippingClass2x")} hint="Shipping class table" />
                        <DimensionSingleSelect label="3x Shipping Class" valueId={form.shippingClass3x[0] || ""} options={shippingOptions} onChange={setSingleLink("shippingClass3x")} hint="Shipping class table" />
                        <DimensionSingleSelect label="4x Shipping Class" valueId={form.shippingClass4x[0] || ""} options={shippingOptions} onChange={setSingleLink("shippingClass4x")} hint="Shipping class table" />
                    </Box>
                    <Typography sx={{ mt: 2.5, mb: 1, fontWeight: 700, fontSize: 13, color: "#64748B" }}>
                        Lookups and formulas
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.5,
                        }}
                    >
                        <LookupField label="Listing Field" value={computed.listingField} />
                        <LookupField label="Internal SKU ID" value={computed.internalSkuId} />
                        <LookupField label="ASIN" value={computed.asin} />
                        <LookupField label="WPID" value={computed.wpid} />
                        <LookupField label="MAC Max Price" value={computed.macMaxPrice} />
                        <LookupField label="BidFTA Max Price" value={computed.bidftaMaxPrice} />
                        <LookupField label="F+F Pricing" value={computed.ffPricing} />
                        <LookupField label="FB MKT Pricing" value={computed.fbMktPricing} />
                        <LookupField label="Purchase Price (eBay)" value={computed.purchasePriceEbay} />
                        <LookupField label="Est. Shipping Cost" value={computed.estShippingCost} />
                        <LookupField label="Cost Rollup 1x" value={computed.costRollup1x} source="Shipping class table" />
                        <LookupField label="Cost Rollup 2x" value={computed.costRollup2x} source="Shipping class table" />
                        <LookupField label="Cost Rollup 3x" value={computed.costRollup3x} source="Shipping class table" />
                        <LookupField label="Cost Rollup 4x" value={computed.costRollup4x} source="Shipping class table" />
                        <LookupField label="Suggested eBay Price" value={computed.suggestedEbayPrice} />
                        <LookupField label="Suggested Walmart Price" value={computed.suggestedWalmartPrice} />
                        <LookupField label="Suggested Margin" value={computed.suggestedMargin} />
                        <LookupField label="Vista Pack Price" value={computed.vistaPackPrice} />
                        <LookupField label="Helper Flag" value={computed.helperFlagWalmartAutomation} />
                        <LookupField label="Listed On" value={computed.listedPlatforms} />
                        <LookupField label="Walmart SKU" value={form.walmartLinkedSku} source="Upload Products" />
                        <LookupField label="eBay SKU" value={form.ebayLinkedSku} source="Upload Products" />
                        <LookupField label="Walmart Price" value={moneyText(form.walmartPrice)} source="Upload Products" />
                        <LookupField label="eBay Price" value={moneyText(form.ebayPrice)} source="Upload Products" />
                        <LookupField label="Positive Examples" value={computed.positiveExampleCount} source="Status Checks table" />
                        <LookupField label="Negative Examples" value={computed.negativeExampleCount} source="Status Checks table" />
                        <LookupField label="Available Qty eBay" value={computed.availableQuantityEbay} />
                        <LookupField label="Available Qty Walmart" value={computed.availableQuantityWalmart} />
                        <LookupField label="eBay Quant 30+ Days" value={computed.ebayQuant30PlusDays} />
                        <LookupField label="WM Quant 30+ Days" value={computed.walmartQuant30PlusDays} />
                        <LookupField
                            label="eBay Older Listing Age"
                            value={computed.ebayOlderListingAge}
                            source="Days since Date Listed"
                        />
                        <LookupField
                            label="WM Older Listing Age"
                            value={computed.walmartOlderListingAge}
                            source="Days since Date Listed"
                        />
                        <LookupField label="Total Quant on Hand" value={computed.totalQuantOnHand} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button
                        onClick={() => {
                            clearLocalPhotos();
                            setDialogOpen(false);
                        }}
                        sx={catalogQuietButtonSx}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={save}
                        startIcon={
                            saving ? <CircularProgress size={14} color="inherit" /> : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <SelectOptionsDialog
                open={Boolean(optionEditor)}
                fieldKey={optionEditor?.fieldKey || ""}
                fieldLabel={optionEditor?.label || ""}
                onClose={() => setOptionEditor(null)}
                onChanged={(change) => {
                    refetchFieldMeta();
                    if (!optionEditor?.fieldKey) return;
                    const key = optionEditor.fieldKey;
                    setForm((prev) => {
                        if (change.type === "rename" && prev[key] === change.previous) {
                            return { ...prev, [key]: change.label };
                        }
                        if (change.type === "remove" && prev[key] === change.label) {
                            return { ...prev, [key]: "" };
                        }
                        if (change.type === "add" && !prev[key]) {
                            return { ...prev, [key]: change.label };
                        }
                        return prev;
                    });
                }}
            />

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                confirming={deleting}
                title={
                    deleteTargets.length === 1
                        ? "Delete product?"
                        : `Delete ${deleteTargets.length} products?`
                }
                description="This cannot be undone. The following will be removed:"
                items={deleteTargets}
            />

            {snack.open && (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            )}
        </>
    );
}
